import React, { useState, useEffect } from 'react';
import { Play, CheckCircle2, AlertCircle, FileText, Loader2, Calculator, Wallet } from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import {
    client,
    currencyCodeToSymbolMap,
    fetchEmployeeList,
    fetchPocketBalance,
    fetchStoresWhereStaffAssigned,
    fetchPayrollAndPayslips
} from '../apiHelper';
import { useToast } from '../hooks/useToast';

export const PayrollMakerManager = ({ storeId }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState(storeId || '');
    const [periodYear, setPeriodYear] = useState(new Date().getFullYear());
    const [periodMonth, setPeriodMonth] = useState(new Date().getMonth() + 1);

    const [eligibleStaff, setEligibleStaff] = useState([]);
    const [walletBalance, setWalletBalance] = useState(0);

    const [deductions, setDeductions] = useState({});

    const activeStore = stores.find(s => Number(s.value) === Number(selectedStoreId));
    const currencySymbol = currencyCodeToSymbolMap[activeStore?.currency] || '₦';

    // 1. Fetch stores on mount
    useEffect(() => {
        const loadStores = async () => {
            try {
                const stored = localStorage.getItem('user');
                const parsed = stored ? JSON.parse(stored) : null;
                if (parsed) {
                    const returnedStores = await fetchStoresWhereStaffAssigned(parsed);
                    const filtered = returnedStores.filter(s => s.storetype !== 'WHS');
                    const mapped = filtered.map(s => ({
                        label: s.identifier,
                        value: s.store_id,
                        currency: s.setccurr
                    }));
                    setStores(mapped);
                    if (!selectedStoreId && mapped.length > 0) {
                        setSelectedStoreId(mapped[0].value);
                    }
                }
            } catch (err) {
                showToast("Failed to load store context.", "error");
            }
        };
        loadStores();
    }, []);

    // 2. Fetch eligible staff and wallet details when parameters change
    const loadEligibilityPreview = async () => {
        if (!selectedStoreId) return;
        setLoading(true);
        try {
            const employees = await fetchEmployeeList(selectedStoreId);
            // console.log(employees)
            setEligibleStaff(employees || []);

            const walletRes = await fetchPocketBalance(selectedStoreId);
            setWalletBalance(walletRes.balance_amount || 0);

            const payslipsRequestObj = {
                store_id: Number(selectedStoreId),
                period_year: Number(periodYear),
                period_month: Number(periodMonth),
            }
            const payrollData = await fetchPayrollAndPayslips(payslipsRequestObj);
            // console.log("Existing payroll & payslips data:", payrollData)

            // Populate existing deductions if available for current run period
            if (payrollData && payrollData.length > 0 && payrollData[0].payslips) {
                const existingDeductionsMap = {};
                payrollData[0].payslips.forEach(p => {
                    if (p.other_deductions && Number(p.other_deductions) > 0) {
                        existingDeductionsMap[p.member_id] = p.other_deductions;
                    }
                });
                setDeductions(existingDeductionsMap);
            } else {
                setDeductions({});
            }
        } catch (err) {
            showToast(err.response?.data?.detail || "Error loading eligible staff list.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedStoreId) {
            loadEligibilityPreview();
        }
    }, [selectedStoreId, periodYear, periodMonth]);

    // Totals preview
    const totalGross = eligibleStaff.reduce((sum, e) => sum + Number(e.gross_salary || 0), 0);
    const estimatedPaye = eligibleStaff.reduce((sum, e) => sum + Number(e.estimated_paye || 0), 0);
    const totalDeductions = eligibleStaff.reduce((sum, e) => sum + Number(deductions[e.users_id] || 0), 0);
    const totalNet = totalGross - estimatedPaye - totalDeductions;
    const hasInsufficientBalance = walletBalance < totalNet;

    // 3. Initiate Payroll Run
    const handleCreatePayrollRun = async () => {
        if (eligibleStaff.length === 0) {
            showToast("Cannot create a run without eligible employees.", "error");
            return;
        }

        if (hasInsufficientBalance) {
            showToast("Insufficient wallet balance to cover the total net payout.", "error");
            return;
        }

        setSubmitting(true);
        try {
            // Package member_id and deduction amount for staff with non-zero deductions
            const formattedDeductions = Object.entries(deductions)
                .filter(([_, amount]) => Number(amount) > 0)
                .map(([userId, amount]) => ({
                    member_id: Number(userId),
                    other_deductions: Number(amount).toFixed(2)
                }));

            const payload = {
                store_id: Number(selectedStoreId),
                period_year: Number(periodYear),
                period_month: Number(periodMonth),
                ...(formattedDeductions.length > 0 && { deductions: formattedDeductions })
            };

            // console.log(payload)
            const res = await client.postData('/payroll/create-payroll-run', payload);
            showToast(`Payroll run created and sent for approval!`, "success");
            loadEligibilityPreview();
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to create payroll run.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeductionChange = (userId, value) => {
        setDeductions(prev => ({
            ...prev,
            [userId]: value
        }));
    };

    const formatCurr = (val) => `${currencySymbol}${Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

    return (
        <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
            <Card className="p-6 bg-white border-zinc-200 shadow-sm">
                <div className="border-b border-zinc-100 pb-4 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                            <Calculator size={20} className="text-blue-600" /> Create Payroll Run (Maker)
                        </h2>
                        <p className="text-xs text-zinc-500">
                            Compile active employee salary structures, calculate PAYE, and generate payslips for approval.
                        </p>
                    </div>

                    <div className="flex items-center gap-2">
                        <select
                            value={selectedStoreId}
                            onChange={(e) => setSelectedStoreId(e.target.value)}
                            className="bg-zinc-50 border border-zinc-200 text-xs rounded-lg px-2.5 py-2 font-medium"
                        >
                            {stores.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>

                        <select
                            value={periodMonth}
                            onChange={(e) => setPeriodMonth(e.target.value)}
                            className="bg-zinc-50 border border-zinc-200 text-xs rounded-lg px-2.5 py-2 font-medium"
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                </option>
                            ))}
                        </select>

                        <input
                            type="number"
                            value={periodYear}
                            onChange={(e) => setPeriodYear(e.target.value)}
                            className="w-20 bg-zinc-50 border border-zinc-200 text-xs rounded-lg px-2.5 py-2 font-medium"
                        />
                    </div>
                </div>

                {/* Summary Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 border border-zinc-100 bg-zinc-50/50 rounded-xl">
                        <span className="text-zinc-500 text-[11px] font-medium block">Eligible Staff</span>
                        <span className="text-xl font-bold text-zinc-900 font-mono">
                            {eligibleStaff.length} {eligibleStaff.length === 1 ? 'Employee' : 'Employees'}
                        </span>
                    </div>
                    <div className="p-4 border border-zinc-100 bg-zinc-50/50 rounded-xl">
                        <span className="text-zinc-500 text-[11px] font-medium block">Estimated Gross</span>
                        <span className="text-xl font-bold text-zinc-900 font-mono">{formatCurr(totalGross)}</span>
                    </div>
                    <div className="p-4 border border-zinc-100 bg-zinc-50/50 rounded-xl">
                        <span className="text-zinc-500 text-[11px] font-medium block">Total PAYE Tax</span>
                        <span className="text-xl font-bold text-amber-600 font-mono">{formatCurr(estimatedPaye)}</span>
                    </div>
                    <div className="p-4 border border-blue-100 bg-blue-50/40 rounded-xl">
                        <span className="text-blue-700 text-[11px] font-semibold block">Net Payable</span>
                        <span className="text-xl font-bold text-blue-900 font-mono">{formatCurr(totalNet)}</span>
                    </div>
                </div>

                {/* Eligible Employee Roster */}
                <div className="space-y-3">
                    <h3 className="text-xs font-bold text-zinc-700 uppercase tracking-wide">
                        Verified Payroll Candidates
                    </h3>

                    {loading ? (
                        <div className="p-8 text-center text-xs text-zinc-500">
                            <Loader2 size={16} className="animate-spin inline mr-2 text-blue-600" />
                            Verifying eligibility and bank accounts...
                        </div>
                    ) : eligibleStaff.length === 0 ? (
                        <div className="p-8 border border-dashed border-zinc-200 rounded-xl text-center text-xs text-zinc-400">
                            No eligible candidates found. Ensure employees have active store assignments, active salary structures, and verified primary bank accounts.
                        </div>
                    ) : (
                        <div className="border border-zinc-200 rounded-xl overflow-hidden">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-500 font-semibold">
                                    <tr>
                                        <th className="p-3">Employee</th>
                                        <th className="p-3">Bank Details</th>
                                        <th className="p-3">Gross Salary</th>
                                        <th className="p-3">Est. PAYE</th>
                                        <th className="p-3">Deduction</th>
                                        <th className="p-3">Est. Net Pay</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-100 font-mono">
                                    {eligibleStaff.map((emp) => {
                                        const gross = Number(emp.gross_salary || 0);
                                        const paye = Number(emp.estimated_paye || 0);
                                        const deductionVal = Number(deductions[emp.users_id] || 0);
                                        const netPay = gross - paye - deductionVal;

                                        return (
                                            <tr key={emp.users_id} className="hover:bg-zinc-50/50">
                                                <td className="p-3 font-sans">
                                                    <p className="font-bold text-zinc-800">{emp.first_name} {emp.last_name}</p>
                                                    <p className="text-[10px] text-zinc-400">{emp.employeeid}</p>
                                                </td>
                                                <td className="p-3 font-sans">
                                                    <p className="text-zinc-700">{emp.bank_name}</p>
                                                    <p className="text-[10px] text-zinc-400 font-mono">{emp.account_number}</p>
                                                </td>
                                                <td className="p-3 text-zinc-800">{formatCurr(gross)}</td>
                                                <td className="p-3 text-amber-600">{formatCurr(paye)}</td>
                                                <td className="p-3 font-sans">
                                                    <Input
                                                        type="number"
                                                        value={deductions[emp.users_id] || ''}
                                                        onChange={(e) => handleDeductionChange(emp.users_id, e.target.value)}
                                                        placeholder="0.00"
                                                        max={gross - paye}
                                                        containerClassName="mb-0 w-28"
                                                        className="py-1 px-2 text-xs border-zinc-300"
                                                    />
                                                </td>
                                                <td className="p-3 font-bold text-emerald-600">{formatCurr(netPay)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer Action */}
                <div className="mt-6 pt-4 border-t border-zinc-100 flex justify-between items-center">
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 text-xs text-zinc-500">
                            <Wallet size={14} className="text-blue-600" />
                            <span>Wallet Available: <strong className="text-zinc-800 font-mono">{formatCurr(walletBalance)}</strong></span>
                        </div>

                        {hasInsufficientBalance && eligibleStaff.length > 0 && (
                            <p className="text-[11px] text-red-600 flex items-center gap-1 font-medium">
                                <AlertCircle size={12} />Insufficient funds to execute this run. Short by {formatCurr(totalNet - walletBalance)}.
                            </p>
                        )}
                    </div>

                    <Button
                        onClick={handleCreatePayrollRun}
                        // --- UPDATE DISABLED CONDITION ---
                        disabled={submitting || eligibleStaff.length === 0 || hasInsufficientBalance}
                        className={`${hasInsufficientBalance && eligibleStaff.length > 0
                            ? "bg-zinc-300 text-zinc-500 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 text-white"
                            } text-xs px-4 py-2 flex items-center gap-2`}
                    >
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                        Generate Run & Request Approval
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default PayrollMakerManager;